// packages/web-ui/src/state/sessions.ts
//
// W2-S8 — `sessions` zustand slice.
//
// Owns the chat-sessions list state (read from `GET /api/v1/chat/sessions`),
// the create-session flow (`POST /api/v1/chat/sessions`), and the hard-delete
// flow (`DELETE /api/v1/chat/sessions/:id`). RB-locked answer #5: hard delete
// only — no soft-archive, no client-side tombstones.
//
// Story scope (per the parent prompt): the slice is independent of the W2-S5
// chat slice. The chat slice's `sessions[]` field (when it lands) can be
// derived from this slice via a selector, or this slice can be merged in a
// later refactor. Keeping it standalone now avoids interfering with parallel
// agents working on the chat slice.
//
// Pattern: matches `state/changeset.ts` (W4-S4) — slice creator returns a
// `StateCreator`, plus a vanilla-store factory for tests/non-shell call-sites.

import { createStore } from 'zustand/vanilla';
import type { StateCreator } from 'zustand';
import type {
  ChatSessionsListResponse,
  CreateSessionResponse,
  SessionSummary,
} from '@tessa-lean/base/api-schemas/index.js';
import type { ApiContext } from '../api/client.js';
import { getDefaultApiContext } from '../api/client.js';
import type { ApiError } from '../api/internal/api-call.js';
import { sessionsApi as defaultSessionsApi, type SessionsApi } from '../api/clients/sessions.js';

/** Per-action lifecycle status — `idle` → `loading` → `ready` | `error`. */
export type SessionsLoadStatus = 'idle' | 'loading' | 'ready' | 'error';

export interface SessionsSliceState {
  /** Sorted list of session summaries (newest-updated first). */
  sessions: SessionSummary[];
  /** Lifecycle of the most-recent `refreshSessions()` call. */
  status: SessionsLoadStatus;
  /** Last refresh error (cleared on next successful refresh). */
  lastError: ApiError | null;
  /** True between `createSession()` dispatch and server confirm. */
  createInFlight: boolean;
  /** When non-null, the delete-confirm dialog is open for this sessionId. */
  pendingDelete: { sessionId: string; isDeleting: boolean } | null;
  /** True between `deleteAllSessions()` dispatch and server confirm. */
  deleteAllInFlight: boolean;
}

export const SESSIONS_INITIAL_STATE: SessionsSliceState = {
  sessions: [],
  status: 'idle',
  lastError: null,
  createInFlight: false,
  pendingDelete: null,
  deleteAllInFlight: false,
};

export interface SessionsSliceActions {
  /** GET /chat/sessions → populate `sessions[]`. Idempotent. */
  refreshSessions: () => Promise<void>;
  /** POST /chat/sessions; on success, prepends to `sessions[]` and returns the new id. */
  createSession: (opts?: { title?: string }) => Promise<string | null>;
  /** Open the hard-delete confirm dialog for the given session. */
  requestDelete: (sessionId: string) => void;
  /** Close the hard-delete confirm dialog without deleting. */
  cancelDelete: () => void;
  /**
   * Hard-delete a single session by id: DELETE /chat/sessions/:id, then remove
   * it from `sessions[]`. Returns `true` on success. Independent of the
   * `pendingDelete` dialog state so non-SessionList callers (e.g. closing a
   * chat tab in the tab bar) can delete without driving the sidebar dialog.
   * RB-locked answer #5: HARD delete — no soft-archive.
   */
  deleteSession: (sessionId: string) => Promise<boolean>;
  /**
   * Confirm the hard-delete: DELETE /chat/sessions/:id, then remove from
   * `sessions[]` and close the dialog. Returns `true` on success.
   * RB-locked answer #5: HARD delete — no soft-archive.
   */
  confirmDelete: () => Promise<boolean>;
  /**
   * Bulk-wipe every saved session via DELETE /chat/sessions. Optimistically
   * clears `sessions[]`; rolls back on server failure. Returns the count of
   * removed session files on success.
   */
  deleteAllSessions: () => Promise<number | null>;
  /**
   * Patch the in-memory `title` for a row without re-fetching. Called by
   * the SSE `chat:sessionRenamed` dispatcher so the SessionList row updates
   * in lockstep with the chat-slice's tab label + ChatPanel header.
   */
  updateSessionTitle: (sessionId: string, title: string) => void;
  /** Test/debug helper. */
  _reset: () => void;
}

export type SessionsSlice = SessionsSliceState & SessionsSliceActions;

export interface SessionsSliceConfig {
  api?: SessionsApi;
  apiContext?: ApiContext;
}

/**
 * Sort summaries by updatedAt descending. The server already returns them
 * pre-sorted (per W1-S9 contract), but we re-sort defensively so the slice
 * doesn't break if the server contract drifts.
 */
function sortByUpdatedAtDesc(list: ChatSessionsListResponse): SessionSummary[] {
  return [...list].sort((a, b) => b.updatedAt.localeCompare(a.updatedAt));
}

export const createSessionsSlice =
  (config: SessionsSliceConfig = {}): StateCreator<SessionsSlice> =>
  (set, get) => {
    const api = config.api ?? defaultSessionsApi;
    const ctx = config.apiContext ?? getDefaultApiContext();

    return {
      ...SESSIONS_INITIAL_STATE,

      refreshSessions: async () => {
        set((s) => ({ ...s, status: 'loading', lastError: null }));
        const res = await api.listSessions(ctx);
        if (res.ok) {
          set((s) => ({
            ...s,
            sessions: sortByUpdatedAtDesc(res.value),
            status: 'ready',
            lastError: null,
          }));
        } else {
          set((s) => ({ ...s, status: 'error', lastError: res.error }));
        }
      },

      createSession: async (opts) => {
        if (get().createInFlight) return null;
        set((s) => ({ ...s, createInFlight: true }));
        const body = opts?.title !== undefined ? { title: opts.title } : {};
        const res = await api.createSession(ctx, body);
        if (!res.ok) {
          set((s) => ({ ...s, createInFlight: false, lastError: res.error }));
          return null;
        }
        const created: CreateSessionResponse = res.value;
        const summary: SessionSummary = {
          sessionId: created.sessionId,
          title: opts?.title,
          createdAt: created.createdAt,
          updatedAt: created.createdAt,
          messageCount: 0,
        };
        set((s) => ({
          ...s,
          sessions: [summary, ...s.sessions.filter((x) => x.sessionId !== summary.sessionId)],
          createInFlight: false,
          lastError: null,
        }));
        return created.sessionId;
      },

      requestDelete: (sessionId) => {
        set((s) => ({ ...s, pendingDelete: { sessionId, isDeleting: false } }));
      },

      cancelDelete: () => {
        set((s) => (s.pendingDelete === null ? s : { ...s, pendingDelete: null }));
      },

      deleteSession: async (sessionId) => {
        const res = await api.deleteSession(ctx, sessionId);
        if (!res.ok) {
          set((s) => ({ ...s, lastError: res.error }));
          return false;
        }
        set((s) => ({
          ...s,
          sessions: s.sessions.filter((x) => x.sessionId !== sessionId),
          lastError: null,
        }));
        return true;
      },

      confirmDelete: async () => {
        const target = get().pendingDelete;
        if (target === null) return false;
        set((s) =>
          s.pendingDelete === null
            ? s
            : { ...s, pendingDelete: { ...s.pendingDelete, isDeleting: true } },
        );
        const ok = await get().deleteSession(target.sessionId);
        if (!ok) {
          set((s) => ({
            ...s,
            pendingDelete:
              s.pendingDelete === null ? null : { ...s.pendingDelete, isDeleting: false },
          }));
          return false;
        }
        set((s) => (s.pendingDelete === null ? s : { ...s, pendingDelete: null }));
        return true;
      },

      deleteAllSessions: async () => {
        if (get().deleteAllInFlight) return null;
        const prevSessions = get().sessions;
        // Optimistically clear; roll back on failure so the UI never drifts
        // from disk.
        set((s) => ({ ...s, sessions: [], deleteAllInFlight: true, lastError: null }));
        const res = await api.deleteAllSessions(ctx);
        if (!res.ok) {
          set((s) => ({
            ...s,
            sessions: prevSessions,
            deleteAllInFlight: false,
            lastError: res.error,
          }));
          return null;
        }
        set((s) => ({ ...s, deleteAllInFlight: false, lastError: null }));
        return res.value.deleted;
      },

      updateSessionTitle: (sessionId, title) => {
        set((s) => {
          const now = new Date().toISOString();
          const idx = s.sessions.findIndex((r) => r.sessionId === sessionId);
          if (idx < 0) {
            // The row isn't in the list yet (e.g. the session was created on a
            // route that never fetched the list, or the rename event beat the
            // create-prepend). Insert an optimistic titled row so the
            // SessionList shows it immediately; the next `refreshSessions()`
            // reconciles the exact `messageCount`/timestamps. A titled session
            // always has at least the first user message, so `messageCount`
            // starts at 1 (never 0 — otherwise the display filter would hide
            // it, see `isDisplayableSession`).
            const inserted: SessionSummary = {
              sessionId,
              title,
              createdAt: now,
              updatedAt: now,
              messageCount: 1,
            };
            return { ...s, sessions: sortByUpdatedAtDesc([inserted, ...s.sessions]) };
          }
          if ((s.sessions[idx]?.title ?? '') === title) return s;
          const next = s.sessions.slice();
          // Bump `updatedAt` so the freshly-titled row re-sorts to the top in
          // lockstep with the server (which stamps `updatedAt` in `setTitle`),
          // instead of staying frozen in its stale list position until reload.
          next[idx] = { ...next[idx]!, title, updatedAt: now };
          return { ...s, sessions: sortByUpdatedAtDesc(next) };
        });
      },

      _reset: () => {
        set({ ...SESSIONS_INITIAL_STATE } as SessionsSlice);
      },
    };
  };

/** Vanilla-store factory for standalone use (tests, non-shell call-sites). */
export function createSessionsStore(config: SessionsSliceConfig = {}) {
  return createStore<SessionsSlice>()(createSessionsSlice(config));
}

/** Pure selector — returns a new array sorted by `updatedAt` descending. */
export function selectSessions(state: SessionsSliceState): SessionSummary[] {
  return state.sessions;
}

/** Pure selector — `true` while the most-recent refresh is in flight. */
export function selectIsLoading(state: SessionsSliceState): boolean {
  return state.status === 'loading';
}

/** Pure selector — error from the most-recent refresh, or `null`. */
export function selectError(state: SessionsSliceState): ApiError | null {
  return state.lastError;
}

/**
 * True when a session is worth showing in the sidebar: it has at least one
 * message OR a real title. This hides the throwaway 0-message, title-less
 * shells that `ChatAutoSelector` mints on a bare `/chat` landing — those are
 * not conversations the user started, and surfacing them made the whole list
 * read as "Untitled / 0 messages". A row keeps showing the instant it gains a
 * title (first turn) or any message.
 */
export function isDisplayableSession(s: SessionSummary): boolean {
  const hasTitle = typeof s.title === 'string' && s.title.trim().length > 0;
  return s.messageCount > 0 || hasTitle;
}

/**
 * The newest not-yet-used (0-message, untitled) session, or `null` when none
 * exists. Exactly the inverse of {@link isDisplayableSession}. The chat
 * auto-selector reuses this instead of minting a fresh empty session on every
 * bare-`/chat` landing, so abandoned visits stop leaking permanent shells.
 * Assumes `sessions` is sorted updatedAt-desc (the slice invariant), so the
 * first match is the most recent blank.
 */
export function selectReusableBlankSession(
  sessions: SessionSummary[],
): SessionSummary | null {
  return sessions.find((s) => !isDisplayableSession(s)) ?? null;
}

/**
 * Pure selector — displayable sessions (see {@link isDisplayableSession}),
 * further narrowed by a case-insensitive substring filter over title + id.
 * Empty shells stay hidden even under an active search query.
 */
export function selectFilteredSessions(
  state: SessionsSliceState,
  query: string,
): SessionSummary[] {
  const visible = state.sessions.filter(isDisplayableSession);
  const q = query.trim().toLowerCase();
  if (q === '') return visible;
  return visible.filter((s) =>
    (s.title ?? '').toLowerCase().includes(q) || s.sessionId.toLowerCase().includes(q),
  );
}
